import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { TestimonialCard } from "@/components/TestimonialCard";
import { AdminTestimonialsList } from "@/components/AdminTestimonialsList";
import { useTestimonials } from "@/hooks/useTestimonials";
import { useAdmin } from "@/hooks/useAdmin";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Star, ExternalLink, MessageSquare } from "lucide-react";
import { SeoHead } from "@/components/SeoHead";
import { LocalBusinessStructuredData } from "@/components/StructuredData";

export default function Avis() {
  const { testimonials, loading } = useTestimonials(false);
  const { isAdmin } = useAdmin();

  return (
    <>
      <SeoHead
        title="Avis Voyageurs - Roca Rio | Témoignages Authentiques"
        description="Découvrez les témoignages authentiques de nos voyageurs sur leurs expériences avec Roca Rio. Des avis sincères sur nos excursions et notre approche du tourisme solidaire à Rio de Janeiro. Note moyenne : 5/5."
        canonical="https://rocario-cleacarioca.com/avis"
        keywords="avis voyageurs, témoignages Roca Rio, avis excursions Rio, avis guide francophone, reviews Rio de Janeiro, tourisme solidaire avis"
      />
      
      <LocalBusinessStructuredData type="TravelAgency" />
      
      <Header />
      
      <main className="min-h-screen bg-background">
        {/* Hero éditorial */}
        <section className="py-20 md:py-28">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <div className="w-12 h-1 bg-roca-orange mb-6" />
              <h1 className="text-4xl md:text-5xl font-playfair font-bold text-foreground mb-4">
                Ce que disent nos voyageurs
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl">
                Des expériences mémorables partagées par ceux qui ont découvert Rio avec nous
              </p>
            </div>
          </div>
        </section>

        {/* Admin Section */}
        {isAdmin && (
          <section className="py-12">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <AdminTestimonialsList />
            </div>
          </section>
        )}

        {isAdmin && <Separator className="my-8" />}

        {/* Testimonials Grid */}
        <section className="py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {/* Google Reviews Banner */}
            <div className="mb-8 rounded-2xl p-6 border border-border/50 shadow-card bg-card">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <div className="text-left">
                    <p className="text-2xl font-bold text-foreground">5.0</p>
                    <p className="text-sm text-muted-foreground">Note Google</p>
                  </div>
                </div>
                <Button 
                  asChild 
                  size="lg" 
                  className="gap-2 bg-primary hover:bg-primary/90 text-primary-foreground shadow-float hover-lift"
                >
                  <a 
                    href="https://share.google/XAL7DUbY4gxkx7qya" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Voir tous nos avis Google
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </Button>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Chargement des avis...</p>
              </div>
            ) : testimonials.length === 0 ? (
              <Card className="max-w-md mx-auto">
                <CardContent className="text-center py-12">
                  <MessageSquare className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    Aucun avis disponible pour le moment.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {testimonials.map((testimonial, index) => (
                  <div 
                    key={testimonial.id} 
                    className="animate-fade-in" 
                    style={{ animationDelay: `${Math.min(index * 0.05, 0.3)}s` }}
                  >
                    <TestimonialCard {...testimonial} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
}
